import { defaultDateFormat } from "@/core/date";
import {
    BrokerState,
    Claim,
    ClaimPagination,
    IdNamePair,
    RelatedClaim,
    RelatedClaimPagination,
    RelatedClaimType,
    SearchableClaim,
} from "@/shared/domain";

import { getSpApi, pagedRequestConfig, requestConfig } from "./apiRoot";
import { UrlManager } from "./UrlManager";

// Swagger: https://ibpcsrcdev01.portima.net/mybrokerapi/swagger/index.html

export const claimMapper = (source: MyBrokerApi.ClaimV2): Claim => {
    return {
        ...source,
        key: `claim-${source.id}-${source.objectType.id}-${source.damageFolderReference}-${source.insurer.fsma}`,
    };
};

export const searchableClaimMapper = (source: Claim[]): SearchableClaim[] => {
    return source?.map((claim) => ({
        ...claim,
        referenceString: `Ref ${claim.reference}`,
        damageEventDateString: defaultDateFormat(claim.damageEventDate),
    }));
};

export const relatedClaimMapper = (source: MyBrokerApi.RelatedClaim, policyholderId: number): RelatedClaim => {
    const { state, date, circumstances, ...sourceRest } = source;
    return {
        ...sourceRest,
        policyholderId,
        circumstance: circumstances as IdNamePair,
        damageEventDate: date,
        brokerState: state as IdNamePair,
    };
};

/**
 * Returns all claims of the logged-in user
 */
export const getAllClaims = async (requestParams: Paths.ApiClaimAll.Get.QueryParameters = {}): Promise<Claim[]> => {
    const axiosRequestConfig = pagedRequestConfig<Paths.ApiClaimAll.Get.QueryParameters>({ params: requestParams });

    const { data } = await getSpApi().get<MyBrokerApi.ClaimV2Payload>(UrlManager.claims(), axiosRequestConfig);
    return data.result.map((item) => claimMapper(item));
};

const claimCategorySubPaths = [
    { id: BrokerState.ALL, path: "all" },
    { id: BrokerState.ON_GOING, path: "ongoing" },
    { id: BrokerState.CLOSED, path: "closed" },
];

export type ClaimCategoryQueryParameters =
    | Paths.ApiClaimAll.Get.QueryParameters
    | Paths.ApiClaimOngoing.Get.QueryParameters
    | Paths.ApiClaimClosed.Get.QueryParameters;

export type AllClaimQueryParameters = Paths.ApiClaimAll.Get.QueryParameters;

export const getRawClaims = async (
    categoryId: string | number = "0",
    requestParams: ClaimCategoryQueryParameters = {},
): Promise<MyBrokerApi.ClaimV2Payload> => {
    const subPath = claimCategorySubPaths.find((category) => category.id === categoryId).path;

    const axiosRequestConfig = pagedRequestConfig<ClaimCategoryQueryParameters>({ params: requestParams });

    const { data } = await getSpApi().get<MyBrokerApi.ClaimV2Payload>(
        UrlManager.filteredClaims(subPath),
        axiosRequestConfig,
    );
    return data;
};

export const getClaims = async (
    requestParams: ClaimCategoryQueryParameters = {},
    categoryId: string | number = "0",
): Promise<ClaimPagination> => {
    const data = await getRawClaims(categoryId, requestParams);
    return { ...data, result: data.result.map((item) => claimMapper(item)) };
};

export const getClaimsLazy = async (requestParams: AllClaimQueryParameters): Promise<ClaimPagination> => {
    const axiosRequestConfig = pagedRequestConfig<AllClaimQueryParameters>({ params: requestParams });
    const { data } = await getSpApi().get<MyBrokerApi.ClaimV2Payload>(
        UrlManager.filteredClaims("all"),
        axiosRequestConfig,
    );
    return {
        ...data,
        result: data.result.map((item) => claimMapper(item)),
    } as ClaimPagination;
};

/**
 * Returns a list of insured good claims
 */
type RelatedClaimQueryParameters =
    | Paths.ApiClaimContract$Id.Get.QueryParameters
    | Paths.ApiClaimInsuredgood$Id.Get.QueryParameters;

export const getRelatedClaims = async (
    id: string,
    featureType: RelatedClaimType,
    isSchoolContract: boolean,
    withClosed: boolean,
    ibpAclId: number,
    policyholderId: number,
    requestParams: RelatedClaimQueryParameters = {},
): Promise<RelatedClaimPagination> => {
    const axiosRequestConfig = pagedRequestConfig<RelatedClaimQueryParameters>({
        params: { ...requestParams, ibpAclId, withClosed, isSchoolContract },
    });

    const { data } = await getSpApi().get<MyBrokerApi.RelatedClaimPayload>(
        UrlManager.relatedClaims(featureType, id),
        axiosRequestConfig,
    );
    return { ...data, result: data.result.map((item) => relatedClaimMapper(item, policyholderId)) };
};

/**
 * Returns a claim details
 */
export const getRawClaim = async (id: number, ibpAclId: number): Promise<MyBrokerApi.ClaimDetails> => {
    const axiosRequestConfig = requestConfig<Paths.ApiClaim$Id.Get.QueryParameters>({
        params: { ibpAclId },
    });

    const { data } = await getSpApi().get<MyBrokerApi.ClaimDetails>(UrlManager.claim(id), axiosRequestConfig);

    return data;
};

export const getClaim = async (id: number, ibpAclId: number): Promise<MyBrokerApi.ClaimDetails> => {
    return await getRawClaim(id, ibpAclId);
};
